/*
 * Copyright (c) 2018 teamapps.org (see code comments for author's name)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * @author ???
 * @author Matthias Bernstein (matthias.bernstein@data-centrics.com)
 */

import * as $ from "jquery";
import {UiComponent} from "./UiComponent";
import {TeamAppsEvent} from "./util/TeamAppsEvent";
import {TeamAppsUiContext} from "./TeamAppsUiContext";
import {executeWhenAttached} from "./util/ExecuteWhenAttached";
import {TeamAppsUiComponentRegistry} from "./TeamAppsUiComponentRegistry";
import * as d3 from "d3";
import {BaseType} from "d3";
import {Selection} from "d3-selection";
import {ScaleContinuousNumeric, ScaleLinear, ScaleTime} from "d3-scale";
import {UiScaleType} from "../generated/UiScaleType";
import {bind} from "./util/Bind";
import {ZoomBehavior, ZoomedElementBaseType} from "d3-zoom";
import {Area, Line} from "d3-shape";
import {Axis} from "d3-axis";
import {Interval, IntervalManager} from "./util/IntervalManager";
import {UiTimeGraphDataPointConfig} from "../generated/UiTimeGraphDataPointConfig";
import {generateUUID} from "./Common";
import {
	UiTimeGraph_DataNeededEvent,
	UiTimeGraph_IntervalSelectedEvent,
	UiTimeGraph_ZoomedEvent,
	UiTimeGraphCommandHandler,
	UiTimeGraphConfig,
	UiTimeGraphEventSource
} from "../generated/UiTimeGraphConfig";
import {createUiLongIntervalConfig, UiLongIntervalConfig} from "../generated/UiLongIntervalConfig";
import {EventFactory} from "../generated/EventFactory";
import {BrushBehavior} from "d3-brush";
import {UiLineChartCurveType} from "../generated/UiLineChartCurveType";
import {UiLineChartLineFormatConfig} from "../generated/UiLineChartLineFormatConfig";
import {createUiColorCssString} from "./util/CssFormatUtil";
import {debouncedMethod, DebounceMode} from "./util/debounce";
import {UiLineChartYScaleZoomMode} from "../generated/UiLineChartYScaleZoomMode";
import {UiLineChartMouseScrollZoomPanMode} from "../generated/UiLineChartMouseScrollZoomPanMode";

type SVGGSelection<DATUM = {}> = Selection<SVGGElement, DATUM, HTMLElement, undefined>;

interface DataPoint {
	x: number,
	y: number
}

function fakeZeroIfLogScale(y: number, scaleType: UiScaleType) {
	if (scaleType === UiScaleType.LOG10 && y === 0) {
		return UiTimeGraph.LOGSCALE_MIN_Y;
	} else {
		return y;
	}
}

export class UiTimeGraph extends UiComponent<UiTimeGraphConfig> implements UiTimeGraphCommandHandler, UiTimeGraphEventSource {

	public readonly onDataNeeded: TeamAppsEvent<UiTimeGraph_DataNeededEvent> = new TeamAppsEvent<UiTimeGraph_DataNeededEvent>(this);
	public readonly onIntervalSelected: TeamAppsEvent<UiTimeGraph_IntervalSelectedEvent> = new TeamAppsEvent<UiTimeGraph_IntervalSelectedEvent>(this);
	public readonly onZoomed: TeamAppsEvent<UiTimeGraph_ZoomedEvent> = new TeamAppsEvent<UiTimeGraph_ZoomedEvent>(this);

	public static readonly LOGSCALE_MIN_Y = 0.5;
	private static readonly yTickFormat = d3.format(",.2s");
	public static readonly DROP_SHADOW_ID = "drop-shadow";

	private yScaleType: UiScaleType;
	private yScaleZoomMode: UiLineChartYScaleZoomMode;
	private mouseScrollZoomPanMode: UiLineChartMouseScrollZoomPanMode;
	private intervalY: UiLongIntervalConfig;
	private intervalX: UiLongIntervalConfig;
	private maxPixelsBetweenDataPoints: number;

	private seriesById: { [id: string]: Series } = {};

	private $main: JQuery;

	private $svg: d3.Selection<SVGElement, {}, null, undefined>;
	private $rootG: SVGGSelection;
	private $dropShadowFilter: Selection<BaseType, {}, null, undefined>;
	private $clipPath: d3.Selection<d3.BaseType, {}, null, undefined>;

	private scaleX: ScaleTime<number, number>;
	private scaleY: ScaleContinuousNumeric<number, number>;

	private dropShadowFilterId: string;

	private margin = {top: 20, right: 15, bottom: 25, left: 30};
	private zoom: ZoomBehavior<ZoomedElementBaseType, any>;
	private xAxis: Axis<Date | number | { valueOf(): number }>;
	private $xAxis: SVGGSelection;
	private yAxis: Axis<number | { valueOf(): number }>;
	private $yAxis: SVGGSelection;
	private zoomLevelIntervalManagers: IntervalManager[];
	private brush: BrushBehavior<number>;
	private $brush: SVGGSelection<number>;
	private $graphClipContainer: Selection<SVGGElement, {}, HTMLElement, undefined>;
	private xSelection: UiLongIntervalConfig;
	private $yZeroLine: Selection<SVGLineElement, {}, HTMLElement, undefined>;

	private get drawableWidth() {
		return this.getWidth() - this.margin.left - this.margin.right
	}

	private get drawableHeight() {
		return this.getHeight() - this.margin.top - this.margin.bottom
	}


	constructor(config: UiTimeGraphConfig, context: TeamAppsUiContext) {
		super(config, context);

		this.maxPixelsBetweenDataPoints = config.maxPixelsBetweenDataPoints;
		this.yScaleType = config.yScaleType;
		this.yScaleZoomMode = config.yScaleZoomMode;

		this.$main = $('<div class="UiTimeGraph" id="' + this.getId() + '">');

		this.scaleX = d3.scaleTime()
			.range([0, this.drawableWidth]);

		this.setYScaleType(config.yScaleType);

		this.scaleY
			.range([this.drawableHeight, 0]);

		this.$svg = d3.select(this.$main[0])
			.append<SVGElement>("svg");
		this.$rootG = this.$svg
			.append<SVGGElement>("g");

		this.setIntervalX(config.intervalX);
		this.setIntervalY(config.intervalY);

		this.$xAxis = this.$rootG.append<SVGGElement>("g")
			.classed("x-axis", true);
		this.xAxis = d3.axisBottom(this.scaleX);

		this.$yAxis = this.$rootG.append<SVGGElement>("g")
			.classed("y-axis", true);
		this.yAxis = d3.axisLeft(this.scaleY);
		this.dropShadowFilterId = `${UiTimeGraph.DROP_SHADOW_ID}-${this.getId()}`;
		let $defs = this.$svg.append<SVGDefsElement>("defs")
			.html(`<filter id="${this.dropShadowFilterId}" y="-50">
	        <feGaussianBlur in="SourceAlpha" stdDeviation="1.5" result="blur"></feGaussianBlur>
	        <feComponentTransfer in="blur" result="fadedBlur">
	            <feFuncA type="linear" slope="0.3"></feFuncA>
	        </feComponentTransfer>
	        <feOffset in="fadedBlur" dx="0.7" dy="1.5" result="offsetBlur"></feOffset>
	        <feMerge>
	            <feMergeNode in="offsetBlur"></feMergeNode>
	            <feMergeNode in="SourceGraphic"></feMergeNode>
	        </feMerge>
	    </filter>`);
		this.$dropShadowFilter = $defs.select(`#${this.dropShadowFilterId}`);
		let clipPathId = this.getId() + "-clipping-path-" + generateUUID();
		this.$clipPath = $defs.append("clipPath")
			.attr("id", clipPathId)
			.append("rect")
			.attr("y", -50);

		this.$graphClipContainer = this.$rootG.append<SVGGElement>("g")
			.classed("graph-clipping-container", true)
			.attr("clip-path", `url(#${clipPathId})`);

		this.$yZeroLine = this.$graphClipContainer.append<SVGLineElement>("line")
			.classed("y-zero-line", true);

		this.zoom = d3.zoom()
			.scaleExtent([1, this.calculateMaxZoomFactor()])
			.on("zoom", () => {
				this.redraw();
				if (this.isDynamicYScaleZooming()) {
					this.animatedZoomYScale();
				}
				this.fireZoomEvent();
			});
		this.setMouseScrollZoomPanMode(config.mouseScrollZoomPanMode);

		Object.keys(config.lineFormats).forEach(seriesId => {
			let lineFormat = config.lineFormats[seriesId];
			this.seriesById[seriesId] = new Series(seriesId, lineFormat, this.$graphClipContainer, this._config.zoomLevels.length, this.dropShadowFilterId);
		});

		this.initZoomLevelIntervalManagers();

		this.brush = d3.brushX<number>()
			.extent([[0, 0], [100, 100] /*provisional values!*/])
			.on("end", this.handleBrushSelection);

		this.$brush = this.$graphClipContainer.append<SVGGElement>("g")
			.classed("brush", true) as SVGGSelection<number>;
		this.$brush
			.call(this.brush);

		this.resetAllData();
	}

	private isDynamicYScaleZooming() {
		return this.yScaleZoomMode === UiLineChartYScaleZoomMode.DYNAMIC || this.yScaleZoomMode === UiLineChartYScaleZoomMode.DYNAMIC_INCLUDING_ZERO;
	}

	private animatedZoomYScale() {
		let tween = (newValue: [number, number]) => {
			return () => {
				// create interpolator and do not show nasty floating numbers
				let i = d3.interpolateArray(this.scaleY.domain().map(date => +date), newValue);

				return (t: number) => {
					this.scaleY.domain(i(t));
					this.redraw();
				}
			}
		};
		let minY: number, maxY: number;
		if (this.yScaleZoomMode === UiLineChartYScaleZoomMode.DYNAMIC) {
			[minY, maxY] = this.getDisplayedDataYBounds();
		} else if (this.yScaleZoomMode === UiLineChartYScaleZoomMode.DYNAMIC_INCLUDING_ZERO) {
			[minY, maxY] = this.getDisplayedDataYBounds();
			if (minY > 0) {
				minY = 0;
			}
			if (maxY < 0) {
				maxY = 0;
			}
		} else {
			minY = this.intervalY.min;
			maxY = this.intervalY.max;
		}
		minY = fakeZeroIfLogScale(minY, this.yScaleType);

		d3.transition(`${this.getId()}-zoomYToDisplayedDomain`)
			.ease(d3.easeLinear)
			.duration(300)
			.tween(`${this.getId()}-zoomYToDisplayedDomain`, tween([minY, maxY]));
	}

	private getDisplayedDataYBounds(): [number, number] {
		let minY = Number.MAX_VALUE;
		let maxY = Number.MIN_VALUE;
		this.getAllSeries().forEach(s => {
			let zoomTransform = d3.zoomTransform(this.$svg.node());
			let transformedScaleX = zoomTransform.rescaleX(this.scaleX as any);
			let bounds = s.getDisplayedDataYBounds(this.getCurrentZoomLevel(), +transformedScaleX.domain()[0], +transformedScaleX.domain()[1], this.yScaleType);
			if (bounds[0] < minY) {
				minY = bounds[0];
			}
			if (bounds[1] > maxY) {
				maxY = bounds[1];
			}
		});
		if (minY >= maxY) {
			this.logger.info(`minY >= maxY! (probably no data yet) ${minY} ${maxY}`);
			minY = 0;
			maxY = 1;
		}
		return [minY, maxY];
	}

	private initZoomLevelIntervalManagers() {
		this.zoomLevelIntervalManagers = [];
		for (let i = 0; i < this._config.zoomLevels.length; i++) {
			this.zoomLevelIntervalManagers[i] = new IntervalManager();
		}
	}

	@executeWhenAttached()
	private redraw() {
		if (this.getWidth() === 0 || this.getHeight() === 0) {
			return;
		}

		let zoomTransform = d3.zoomTransform(this.$svg.node());
		let transformedScaleX = zoomTransform.rescaleX(this.scaleX as any);

		this.$yZeroLine
			.attr("x1", 0)
			.attr("y1", this.scaleY(0))
			.attr("x2", this.getWidth())
			.attr("y2", this.scaleY(0))
			.attr("visibility", this.scaleY.domain()[0] === 0 ? "hidden" : "visible");

		this.$xAxis.call(this.xAxis.scale(transformedScaleX));
		this.$yAxis.call(this.yAxis);
		let $ticks = this.$yAxis.node().querySelectorAll('.tick');
		for (let i = 0; i < $ticks.length; i++) {
			let $text: SVGTextElement = $ticks[i].querySelector("text");
			let querySelector: any = $ticks[i].querySelector('line');
			if ($text.innerHTML === '') {
				querySelector.setAttribute("visibility", 'hidden');
			} else {
				querySelector.setAttribute("visibility", 'visible');
			}
		}

		let domain = this.restrictDomainXToConfiguredInterval(transformedScaleX.domain());

		const zoomLevel = this.getCurrentZoomLevel();
		// this.logger.debug("millisecondsPerPixel: " + millisecondsPerPixel + " zoomLevel: " + zoomLevel + " at scale: " + zoomTransform.k);

		let uncoveredIntervals = this.zoomLevelIntervalManagers[zoomLevel].getUncoveredIntervals(new Interval(domain[0], domain[1]));
		if (uncoveredIntervals.length > 0) {
			for (let uncoveredInterval of uncoveredIntervals) {
				if (uncoveredInterval.start >= uncoveredInterval.end) {
					continue; // do not request empty intervals. may happen if the domainX is [0, 0]
				}
				this.logger.debug("firing onDataNeeded: " + uncoveredInterval);
				this.onDataNeeded.fire(EventFactory.createUiTimeGraph_DataNeededEvent(this.getId(), zoomLevel, createUiLongIntervalConfig(uncoveredInterval.start, uncoveredInterval.end)));
				this.zoomLevelIntervalManagers[zoomLevel].addInterval(new Interval(uncoveredInterval.start, uncoveredInterval.end));
			}
		}

		this.getAllSeries().forEach(series => {
			series.draw(zoomLevel, domain[0], domain[1], transformedScaleX, this.scaleY, this.yScaleType);
		});

		if (this.xSelection) {
			let brushX1 = Math.max(-10, transformedScaleX(this.xSelection.min));
			let brushX2 = Math.min(this.drawableWidth + 10, transformedScaleX(this.xSelection.max));
			if (brushX2 < 0 || brushX1 > this.drawableWidth) {
				this.brush.move(this.$brush, null);
			} else {
				this.brush.move(this.$brush, [brushX1, brushX2]);
			}
		} else {
			this.brush.move(this.$brush, null);
		}
	}

	private restrictDomainXToConfiguredInterval(domain: [number, number]) {
		return [Math.max(this.intervalX.min, +(domain[0])), Math.min(this.intervalX.max, +(domain[1]))];
	}

	private getCurrentZoomLevel() {
		let zoomTransform = d3.zoomTransform(this.$svg.node());
		let transformedScaleX = zoomTransform.rescaleX(this.scaleX as any);
		let domain = this.restrictDomainXToConfiguredInterval(transformedScaleX.domain());

		const millisecondsPerPixel = (domain[1] - domain[0]) / this.drawableWidth;
		let sortedZoomLevels = this.getSortedZoomLevels();
		let zoomLevelToApply = sortedZoomLevels
			.filter(indexedZoomLevel => {
				let minMillisecondsPerPixels = indexedZoomLevel.zoomLevel.approximateMillisecondsPerDataPoint / this.maxPixelsBetweenDataPoints;
				return minMillisecondsPerPixels <= millisecondsPerPixel;
			})[0] || sortedZoomLevels[sortedZoomLevels.length - 1];
		return zoomLevelToApply.index;
	}

	private getSortedZoomLevels() {
		return this._config.zoomLevels
			.map((zoomLevel, index) => {
				return {index, zoomLevel}
			})
			.sort((z1, z2) => z2.zoomLevel.approximateMillisecondsPerDataPoint - z1.zoomLevel.approximateMillisecondsPerDataPoint);
	}

	@bind
	private handleBrushSelection() {
		let zoomTransform = d3.zoomTransform(this.$svg.node());
		let transformedScaleX = zoomTransform.rescaleX(this.scaleX as any);

		if (d3.event.sourceEvent == null || d3.event.sourceEvent.type === "zoom") {
			return;  // handle only user-triggered brush changes!
		}
		let brushSelection = d3.brushSelection(this.$brush.node());
		if (brushSelection != null) {
			this.xSelection = createUiLongIntervalConfig(+transformedScaleX.invert(brushSelection[0] as number), +transformedScaleX.invert(brushSelection[1] as number));
			this.onIntervalSelected.fire(EventFactory.createUiTimeGraph_IntervalSelectedEvent(this.getId(), this.xSelection));
		} else {
			this.xSelection = null;
			this.onIntervalSelected.fire(EventFactory.createUiTimeGraph_IntervalSelectedEvent(this.getId(), createUiLongIntervalConfig(this.intervalX.min, this.intervalX.max)));
		}
	}

	private updateYAxisTickFormat() {
		if (this.yScaleType === UiScaleType.LOG10) {
			let numberOfYTickGroups = Math.log10(this.scaleY.domain()[1]) + 1;
			let heightPerYTickGroup = this.getHeight() / numberOfYTickGroups;
			this.yAxis.tickFormat((value: number, i: number) => {
				if (value < 1) {
					return "";
				} else {
					if (heightPerYTickGroup >= 150) {
						return UiTimeGraph.yTickFormat(value);
					} else if (heightPerYTickGroup >= 80) {
						let firstDigitOfValue = Number(("" + value)[0]);
						return firstDigitOfValue <= 5 ? UiTimeGraph.yTickFormat(value) : "";
					} else if (heightPerYTickGroup >= 30) {
						let firstDigitOfValue = Number(("" + value)[0]);
						return firstDigitOfValue === 1 || firstDigitOfValue === 5 ? UiTimeGraph.yTickFormat(value) : "";
					} else {
						let firstDigitOfValue = Number(("" + value)[0]);
						return firstDigitOfValue === 1 ? UiTimeGraph.yTickFormat(value) : "";
					}
				}
			});
		} else {
			this.yAxis.tickFormat((domainValue: number) => {
				if (domainValue < 1 && domainValue >= .1 || domainValue > -1 && domainValue <= -.1) {
					return d3.format(",.1")(domainValue);
				} else {
					return UiTimeGraph.yTickFormat(domainValue)
				}
			})
				.ticks(this.getHeight() / 20);
		}
	}

	public onResize(): void {
		if (this.getWidth() === 0 && this.getHeight() === 0) {
			return;
		}
		this.scaleX.range([0, this.drawableWidth]);
		this.scaleY.range([this.drawableHeight, 0]);
		this.updateZoomExtents();

		this.$xAxis.attr("transform", "translate(0," + this.drawableHeight + ")");
		this.updateYAxisTickFormat();

		this.$dropShadowFilter.attr("width", this.drawableWidth)
			.attr("height", this.getHeight() + 100);
		this.$clipPath.attr("width", this.drawableWidth)
			.attr("height", this.getHeight() + 100);

		this.brush.extent([[0, -5], [this.drawableWidth, this.drawableHeight]]);
		this.$brush.call(this.brush); // update size...

		this.redraw();
	}

	private updateZoomExtents() {
		if (this.zoom == null) { // not yet initialized
			return;
		}
		this.zoom
			.translateExtent([[0, -Infinity], [this.getWidth() /*zoom is attached to $svg, remember!*/, Infinity]])
			.scaleExtent([1, this.calculateMaxZoomFactor()]);
	}

	private getAllSeries() {
		return Object.keys(this.seriesById).map(id => this.seriesById[id]);
	}

	addData(zoomLevel: number, intervalX: UiLongIntervalConfig, data: { [seriesId: string]: UiTimeGraphDataPointConfig[] }): void {
		this.zoomLevelIntervalManagers[zoomLevel].addInterval(new Interval(intervalX.min, intervalX.max));
		Object.keys(data).forEach(seriesId => {
			let seriesData = data[seriesId];
			this.seriesById[seriesId] && this.seriesById[seriesId].addData(zoomLevel, intervalX, seriesData);
		});
		this.redraw();
		if (this.isDynamicYScaleZooming()) {
			this.animatedZoomYScale();
		}
	}

	resetAllData(): void {
		this.initZoomLevelIntervalManagers();
		this.getAllSeries().forEach(d => d.resetData());
		this.redraw();
	}

	@debouncedMethod(500, DebounceMode.BOTH)
	replaceAllData(zoomLevel: number, intervalX: UiLongIntervalConfig, data: { [seriesId: string]: UiTimeGraphDataPointConfig[] }): void {
		this.initZoomLevelIntervalManagers();
		this.getAllSeries().forEach(d => d.resetData());
		this.addData(zoomLevel, intervalX, data);
	}

	setIntervalX(intervalX: UiLongIntervalConfig): void {
		this.intervalX = intervalX;
		this.updateZoomExtents();
		this.scaleX.domain([this.intervalX.min, this.intervalX.max]);
	}

	setIntervalY(intervalY: UiLongIntervalConfig): void {
		this.intervalY = intervalY;
		this.updateMarginLeftCorrespondingToNeededLabelWidths();
		this.animatedZoomYScale();
	}

	private updateMarginLeftCorrespondingToNeededLabelWidths() {
		this.margin.left = (this.intervalY.max > 10000 || this.intervalY.min < 10) ? 37
			: (this.intervalY.max > 100) ? 30
				: 25;
		this.$rootG.attr("transform", "translate(" + this.margin.left + "," + this.margin.top + ")");
		this.redraw();
	}

	public setYScaleZoomMode(yScaleZoomMode: UiLineChartYScaleZoomMode): void {
		this.yScaleZoomMode = yScaleZoomMode;
		this.animatedZoomYScale();
	}

	setMouseScrollZoomPanMode(mouseScrollZoomPanMode: UiLineChartMouseScrollZoomPanMode): void {
		this.mouseScrollZoomPanMode = mouseScrollZoomPanMode;

		this.$svg.on('.zoom', null);
		this.$svg.call(this.zoom);

		let originalZoomWeelHandler = this.$svg.on("wheel.zoom");
		let me = this;
		this.$svg.on("wheel.zoom", function () {
			if (me.mouseScrollZoomPanMode === UiLineChartMouseScrollZoomPanMode.DISABLED) {
				return;
			} else if (me.mouseScrollZoomPanMode === UiLineChartMouseScrollZoomPanMode.WITH_MODIFIER_KEY && !(<MouseEvent>d3.event).ctrlKey && !(<MouseEvent>d3.event).altKey && !(<MouseEvent>d3.event).shiftKey && !(<MouseEvent>d3.event).metaKey) {
				return;
			}
			originalZoomWeelHandler.apply(this, arguments);
			me.zoom.translateBy(me.$svg, d3.event.wheelDeltaX / d3.zoomTransform(me.$svg.node()).k / 2, 0);
			d3.event.preventDefault(); // prevent MacOS' swipe pages back and forward
		});
	}

	@debouncedMethod(500, DebounceMode.LATER)
	private fireZoomEvent() {
		let zoomTransform = d3.zoomTransform(this.$svg.node());
		let transformedScaleX = zoomTransform.rescaleX(this.scaleX as any);
		let domain = transformedScaleX.domain();
		let currentZoomLevel = this.getCurrentZoomLevel();
		this.onZoomed.fire(EventFactory.createUiTimeGraph_ZoomedEvent(this.getId(), createUiLongIntervalConfig(+domain[0], +domain[1]), currentZoomLevel));
	}

	setYScaleType(yScaleType: UiScaleType): void {
		if (yScaleType === UiScaleType.LOG10) {
			this.scaleY = d3.scaleLog();
		} else {
			this.scaleY = d3.scaleLinear();
		}
		if (this.attachedToDom) {
			let domainMin = fakeZeroIfLogScale(this.intervalY.min, this.yScaleType);
			this.scaleY.domain([domainMin, this.intervalY.max]);
			this.reLayout(true);
		}
	}

	setSelectedInterval(intervalX: UiLongIntervalConfig): void {
		this.xSelection = intervalX;
		this.redraw();
	}

	setMaxPixelsBetweenDataPoints(maxPixelsBetweenDataPoints: number): void {
		this.maxPixelsBetweenDataPoints = maxPixelsBetweenDataPoints;
		this.updateZoomExtents();
		this.redraw();
	}

	destroy(): void {
		// nothing to do!
	}

	getMainDomElement(): JQuery {
		return this.$main;
	}

	private calculateMaxZoomFactor(): number {
		if (this.getWidth() === 0 || this.getHeight() === 0) {
			return 1;
		}

		let displayedMilliseconds = this.intervalX.max - this.intervalX.min;
		let sortedZoomLevels = this.getSortedZoomLevels();
		let highestZoomLevelApproximateMillisPerDataPoint = sortedZoomLevels[sortedZoomLevels.length - 1].zoomLevel.approximateMillisecondsPerDataPoint;

		return this.maxPixelsBetweenDataPoints * displayedMilliseconds / (this.drawableWidth * highestZoomLevelApproximateMillisPerDataPoint);
	}

	setLineFormats(lineFormats: { [lineId: string]: UiLineChartLineFormatConfig }): void {
		Object.keys(lineFormats).forEach(lineId => {
			let lineFormat = lineFormats[lineId];
			let existingSeries = this.seriesById[lineId];
			if (existingSeries) {
				existingSeries.setLineFormat(lineFormat);
			} else {
				this.seriesById[lineId] = new Series(lineId, lineFormat, this.$graphClipContainer, this._config.zoomLevels.length, this.dropShadowFilterId);
			}
		});
		Object.keys(this.seriesById).forEach(lineId => {
			if (!lineFormats[lineId]) {
				this.seriesById[lineId].destroy();
				delete this.seriesById[lineId];
			}
		});
		this.redraw();
	}
}

var CurveTypeToCurveFactory = {
	[UiLineChartCurveType.LINEAR]: d3.curveLinear,
	[UiLineChartCurveType.STEP]: d3.curveStep,
	[UiLineChartCurveType.STEPBEFORE]: d3.curveStepBefore,
	[UiLineChartCurveType.STEPAFTER]: d3.curveStepAfter,
	[UiLineChartCurveType.BASIS]: d3.curveBasis,
	[UiLineChartCurveType.CARDINAL]: d3.curveCardinal,
	[UiLineChartCurveType.MONOTONE]: d3.curveMonotoneX,
	[UiLineChartCurveType.CATMULLROM]: d3.curveCatmullRom,
}

class Series {
	private zoomLevelData: UiTimeGraphDataPointConfig[][] = [];
	private line: Line<DataPoint>;
	private $line: Selection<SVGPathElement, {}, HTMLElement, undefined>;
	private area: Area<DataPoint>;
	private $area: Selection<SVGPathElement, {}, HTMLElement, undefined>;
	private $dots: d3.Selection<SVGGElement, {}, HTMLElement, undefined>;
	private $defs: Selection<SVGDefsElement, {}, HTMLElement, undefined>;
	private colorScale: ScaleLinear<string, string>;
	private $main: Selection<SVGGElement, {}, HTMLElement, undefined>;

	constructor(
		private id: string,
		private lineFormat: UiLineChartLineFormatConfig,
		$container: SVGGSelection,
		private numberOfZoomLevels: number,
		private dropShadowFilterId: string,
	) {
		this.$main = $container
			.append<SVGGElement>("g")
			.attr("data-series-id", id);

		this.initDomNodes();
		this.resetData();
	}

	private initDomNodes() {
		this.area = d3.area<DataPoint>()
			.curve(CurveTypeToCurveFactory[this.lineFormat.graphType]);
		this.$area = this.$main.append<SVGPathElement>("path")
			.classed("area", true)
			.attr("fill", `url(#area-gradient-${this.id})`);
		this.line = d3.line<DataPoint>()
			.curve(CurveTypeToCurveFactory[this.lineFormat.graphType]);
		this.$line = this.$main.append<SVGPathElement>("path")
			.classed("line", true)
			.attr("stroke", `url(#line-gradient-${this.id})`)
			.style("filter", `url("#${this.dropShadowFilterId}")`);
		this.$dots = this.$main.append<SVGGElement>("g")
			.classed("dots", true);

		this.colorScale = d3.scaleLinear<string, string>()
			.range([createUiColorCssString(this.lineFormat.lineColorScaleMin), createUiColorCssString(this.lineFormat.lineColorScaleMax)]);
		this.$defs = this.$main.append<SVGDefsElement>("defs")
			.html(`<linearGradient class="line-gradient" id="line-gradient-${this.id}" x1="0" x2="0" y1="0" y2="100" gradientUnits="userSpaceOnUse">
	        <stop stop-color="${createUiColorCssString(this.lineFormat.lineColorScaleMax)}" offset="0" />
	        <stop stop-color="${createUiColorCssString(this.lineFormat.lineColorScaleMin)}" offset="1" />
	    </linearGradient>
	    <linearGradient class="area-gradient" id="area-gradient-${this.id}" x1="0" x2="0" y1="0" y2="100" gradientUnits="userSpaceOnUse">
	        <stop stop-color="${createUiColorCssString(this.lineFormat.areaColorScaleMax)}" offset="0" />
	        <stop stop-color="${createUiColorCssString(this.lineFormat.areaColorScaleMin)}" offset="1" />
	    </linearGradient>`);
	}

	public resetData(): any {
		this.zoomLevelData = [];
		for (let i = 0; i < this.numberOfZoomLevels; i++) {
			this.zoomLevelData[i] = [];
		}
	}

	public getDisplayedData(zoomLevel: number, xStart: number, xEnd: number, scale: UiScaleType): DataPoint[] {
		let i = 0;
		for (; i < this.zoomLevelData[zoomLevel].length; i++) {
			if (this.zoomLevelData[zoomLevel][i].x > xStart) {
				break;
			}
		}
		let startIndex = i === 0 ? 0 : i - 1;
		for (; i < this.zoomLevelData[zoomLevel].length; i++) {
			if (this.zoomLevelData[zoomLevel][i].x >= xEnd) {
				break;
			}
		}
		let endIndex = i === this.zoomLevelData[zoomLevel].length ? i : i + 1;


		let data = this.zoomLevelData[zoomLevel].slice(startIndex, endIndex);
		// if (data.length > 200) {
		// 	data.forEach(d => (d as any).date = new Date(d.x).toString());
		// 	console.log(`zoomLevel: ${zoomLevel}; interval: ${new Date(xStart).toString()}-${new Date(xEnd).toString()}; layer: ${this.zoomLevelData[zoomLevel].length}; returned: ${data.length}`, data);
		// }
		return data;
	}

	public getDisplayedDataYBounds(zoomLevel: number, xStart: number, xEnd: number, scale: UiScaleType): [number, number] {
		let minY = Number.MAX_VALUE;
		let maxY = Number.MIN_VALUE;
		let displayedData = this.getDisplayedData(zoomLevel, xStart, xEnd, scale);
		displayedData.forEach(d => {
			if (d.y < minY) {
				minY = d.y;
			}
			if (d.y > maxY) {
				maxY = d.y;
			}
		});
		return [minY, maxY];
	}

	public addData(zoomLevel: number, intervalX: UiLongIntervalConfig, data: UiTimeGraphDataPointConfig[]) {
		let i = 0, j = 0;
		while (i < this.zoomLevelData[zoomLevel].length && j < data.length) {
			if (this.zoomLevelData[zoomLevel][i].x == data[j].x) {
				this.zoomLevelData[zoomLevel][i] = data[j];
				i++;
				data.splice(j, 1);
			} else if (this.zoomLevelData[zoomLevel][i].x < data[j].x) {
				i++;
			} else {
				j++;
			}
		}

		this.zoomLevelData[zoomLevel] = this.zoomLevelData[zoomLevel].concat(data);
		this.zoomLevelData[zoomLevel].sort((a, b) => a.x - b.x);
	}

	public draw(zoomLevel: number, xStart: number, xEnd: number, scaleX: ScaleTime<number, number>, scaleY: ScaleContinuousNumeric<number, number>, yScaleType: UiScaleType) {
		this.colorScale.domain(scaleY.domain());
		this.$defs.select(".line-gradient").attr("y2", scaleY.range()[0]);
		this.$defs.select(".area-gradient").attr("y2", scaleY.range()[0]);

		let data = this.getDisplayedData(zoomLevel, xStart, xEnd, yScaleType);
		this.line
			.x(d => scaleX(d.x))
			.y(d => scaleY(fakeZeroIfLogScale(d.y, yScaleType)));
		this.$line.attr("d", this.line(data));

		if (this.lineFormat.areaColorScaleMin != null && this.lineFormat.areaColorScaleMin.alpha > 0
			|| this.lineFormat.areaColorScaleMax != null && this.lineFormat.areaColorScaleMax.alpha > 0) { // do not render transparent area!
			this.area
				.x(d => scaleX(d.x))
				.y0(scaleY.range()[0])
				.y1(d => scaleY(fakeZeroIfLogScale(d.y, yScaleType)));
			this.$area.attr("d", this.area(data));
		}

		let $dotsDataSelection = this.$dots.selectAll<SVGCircleElement, UiTimeGraphDataPointConfig>(".dot")
			.data(this.lineFormat.dataDotRadius > 0 ? data : [])
			// .attr("fill", d => this.colorScale(d.y))
			.attr("cx", d => scaleX(d.x))
			.attr("cy", d => scaleY(fakeZeroIfLogScale(d.y, yScaleType)));
		$dotsDataSelection
			.enter().append("circle") // Uses the enter().append() method
			.attr("class", "dot") // Assign a class for styling
			// .attr("fill", d => this.colorScale(d.y))
			.attr("cx", d => scaleX(d.x))
			.attr("cy", d => scaleY(fakeZeroIfLogScale(d.y, yScaleType)))
			.attr("r", this.lineFormat.dataDotRadius);
		$dotsDataSelection.exit().remove();
	}

	setLineFormat(lineFormat: UiLineChartLineFormatConfig) {
		this.lineFormat = lineFormat;
		this.$main.html("");
		this.initDomNodes();
	}

	public destroy() {
		this.$main.remove();
	}
}

TeamAppsUiComponentRegistry.registerComponentClass("UiTimeGraph", UiTimeGraph);